import { normaliseDocument } from '../model/document';
import type { ModelDocument } from '../model/types';

export const AUTOSAVE_KEY = 'constructcanvas.autosave.v1';

function safeFileName(value: string): string {
  const cleaned = value.trim().replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'constructcanvas-model';
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadModel(document: ModelDocument): void {
  const payload = JSON.stringify(document, null, 2);
  downloadBlob(new Blob([payload], { type: 'application/vnd.constructcanvas+json' }), `${safeFileName(document.name)}.ccmodel`);
}

export async function readModelFile(file: File): Promise<ModelDocument> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('The selected file is not valid JSON.');
  }
  return normaliseDocument(parsed);
}

const OPFS_AUTOSAVE_FILE = 'constructcanvas-autosave.ccmodel';

async function opfsRoot(): Promise<FileSystemDirectoryHandle | null> {
  try {
    if (!navigator.storage || typeof navigator.storage.getDirectory !== 'function') return null;
    return await navigator.storage.getDirectory();
  } catch {
    return null;
  }
}

export async function saveAutosave(document: ModelDocument): Promise<void> {
  const payload = JSON.stringify(document);
  try {
    const root = await opfsRoot();
    if (root) {
      const handle = await root.getFileHandle(OPFS_AUTOSAVE_FILE, { create: true });
      const writable = await handle.createWritable();
      await writable.write(payload);
      await writable.close();
    }
    localStorage.setItem(AUTOSAVE_KEY, payload);
  } catch (error) {
    console.warn('ConstructCanvas autosave failed', error);
    try { localStorage.setItem(AUTOSAVE_KEY, payload); } catch { /* Storage may be unavailable. */ }
  }
}

export async function readAutosave(): Promise<ModelDocument | null> {
  try {
    const root = await opfsRoot();
    if (root) {
      try {
        const handle = await root.getFileHandle(OPFS_AUTOSAVE_FILE);
        const file = await handle.getFile();
        return normaliseDocument(JSON.parse(await file.text()));
      } catch {
        // Fall through to the localStorage compatibility copy.
      }
    }
    const value = localStorage.getItem(AUTOSAVE_KEY);
    return value ? normaliseDocument(JSON.parse(value)) : null;
  } catch {
    return null;
  }
}
